import { SelectGroupRole } from "@components/role/select-group-role";
import { MemberRoleReadOnly } from "@components/table/members/members-column/role-column/member-role-read-only";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { MemberIdentityRoleProps } from "@custom-types/member-types";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";

interface Props extends BaseCompanyIdProps, MemberIdentityRoleProps {
  /** The selected group */
  selectedGroup: FaroCoreApiTypes.GroupDetailResponse;

  /** Forces rendering a read-only role label instead of a dropdown */
  isReadOnly?: boolean;

  /** Location from which the member list is rendered, for tracking purposes. */
  location: "role-column";
}

/** Render and adjust member roles in group context */
export function GroupMemberRole({
  companyId,
  memberIdentityRole,
  selectedGroup,
  isReadOnly = false,
  location,
}: Props): JSX.Element {
  const currentUser = useAppSelector(currentUserSelector);
  const { canEditMember } = useHasUserValidRoleGroupLevel({ selectedGroup });

  const { changeMemberRole } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "group",
    memberIdentity: memberIdentityRole.identity,
    currentRole: memberIdentityRole.role,
    group: selectedGroup,
  });

  const isCurrentUser = currentUser?.identity === memberIdentityRole.identity;
  const isCurrentUserGM = currentUser?.role === "COMPANY_MANAGER";

  if (isReadOnly) {
    return (
      <MemberRoleReadOnly role={memberIdentityRole.role} variant="tableCell" />
    );
  }

  /**
   * Return non-editable member role if:
   * - Member is current logged user and the role is group manager,
   *   as he might loose access to the group
   * - User has no permission to edit member
   */
  if ((isCurrentUser && isCurrentUserGM) || !canEditMember) {
    return (
      <span style={{ fontSize: DEFAULT_TEXT_FONT_SIZE }}>
        {formatUserRoleType(memberIdentityRole.role)}
      </span>
    );
  }

  /** Trigger updating member role */
  async function onRoleChange(role: FaroCoreApiTypes.GroupRole): Promise<void> {
    try {
      await changeMemberRole(role);
    } catch {
      // Errors are handled by the hook and slice
    }
  }

  return (
    <SelectGroupRole
      selectedRole={memberIdentityRole.role as FaroCoreApiTypes.GroupRole}
      onChange={onRoleChange}
      variant="tableCell"
    />
  );
}
